import type Phaser from "phaser";
import { CANVAS_COLOURS, type CanvasColour, type LayerHighlight, type PresenceArea } from "daedalus-core";
import { forEachBoundaryRun, forEachRun, type CellWindow } from "./cell-runs.ts";

/**
 * Highlight layer on the map (ADR-070's amendment of 2026-10-04, decisions 7 and 8): the active layer's cells
 * and things over every layer, in three looks that differ by pattern and by weight, never by hue alone. Cells
 * that show are the loud one: a 75% fill in `layerHighlight` inside an edge in the colour (2 px, 3 px below
 * half zoom, where cells are small) and a 1 px dark keyline outside it. Cells covered by a tile or prop above
 * are quieter: a faint fill in `layerHighlightCovered` under two half-strength stripes per cell row, with a
 * softer edge and the keyline. Cells only estimated to be under a tile or prop are quieter still: the faint
 * fill alone in `layerHighlightUnder` with a 1 px soft edge and no keyline. One `Graphics`, a rectangle per run
 * of cells and never an object per cell, drawn for a window half a view wider than the camera's view on every
 * side and again only when the highlight or the zoom changes or the view leaves that window. `sync`, called
 * every frame, compares numbers and allocates nothing.
 */

/**
 * The opacity of the fill over cells that show.
 */
export const HIGHLIGHT_FILL = 0.75;

/**
 * The opacity of the fill under the stripes of covered cells, and of cells under a tile or prop.
 */
export const HIGHLIGHT_COVERED_FILL = 0.18;

/**
 * The opacity of a covered cell's stripes.
 */
export const HIGHLIGHT_STRIPE = 0.5;

/**
 * The opacity of covered cells too small on screen to stripe: about what the fill and the stripes average to.
 */
export const HIGHLIGHT_COVERED_FLAT = 0.38;

/**
 * The opacity of the edge round covered cells and round cells under a tile or prop.
 */
export const HIGHLIGHT_QUIET_EDGE = 0.6;

/**
 * The smallest cell on screen, in pixels, that carries its stripes.
 */
export const HIGHLIGHT_STRIPE_MIN_CELL = 6;

/**
 * The width of the edge round showing and covered cells, in screen pixels.
 */
export const HIGHLIGHT_EDGE_PX = 2;

/**
 * The width of the edge round showing cells below `HIGHLIGHT_FAR_ZOOM`, in screen pixels.
 */
export const HIGHLIGHT_FAR_EDGE_PX = 3;

/**
 * The zoom below which showing cells take the wider edge.
 */
export const HIGHLIGHT_FAR_ZOOM = 0.5;

/**
 * The width of the edge round cells under a tile or prop, in screen pixels.
 */
export const HIGHLIGHT_UNDER_EDGE_PX = 1;

/**
 * The width of the dark keyline outside the edge, in screen pixels.
 */
export const HIGHLIGHT_KEYLINE_PX = 1;

/**
 * What the drawing needs of a `Graphics`: a fill style and a rectangle.
 */
export interface HighlightPen {
  fillStyle(color: number, alpha?: number): unknown;
  fillRect(x: number, y: number, width: number, height: number): unknown;
}

/**
 * How one of a highlight's bitmaps is drawn: its colour, its fill's opacity, its stripes' opacity (0 for
 * none) and the fill that stands in for them on small cells, its edge's width in screen pixels and opacity,
 * and whether a keyline runs outside it.
 */
interface AreaLook {
  readonly colour: CanvasColour;
  readonly fill: number;
  readonly stripe: number;
  readonly flat: number;
  readonly edgePx: number;
  readonly edge: number;
  readonly keyline: boolean;
}

/**
 * Fills a strip along each boundary run of an area, inside the boundary or outside it. A strip outside runs on
 * past both ends of its run by its width, so the strips meet at a convex corner.
 *
 * @param pen The pen.
 * @param area The area.
 * @param window The window of cells.
 * @param cell A cell's size in world pixels.
 * @param width The strip's width in world pixels.
 * @param inside Whether the strip lies inside the boundary.
 * @returns How many rectangles were drawn.
 */
function stripBoundary(pen: HighlightPen, area: PresenceArea, window: CellWindow, cell: number, width: number, inside: boolean): number {
  const run = inside ? 0 : width;
  return forEachBoundaryRun(area, window, (side, x, y, length) => {
    const left = x * cell;
    const top = y * cell;
    if (side === "top" || side === "bottom") {
      const edge = side === "top" ? top : top + cell;
      pen.fillRect(left - run, (side === "top") === inside ? edge : edge - width, length * cell + 2 * run, width);
    } else {
      const edge = side === "left" ? left : left + cell;
      pen.fillRect((side === "left") === inside ? edge : edge - width, top - run, width, length * cell + 2 * run);
    }
  });
}

/**
 * Draws one of a highlight's bitmaps inside a window: the keyline outside its boundary when its look has one,
 * its fill, its stripes when it has them and its cells are large enough on screen, and its edge.
 *
 * @param pen The pen.
 * @param area The bitmap.
 * @param window The window of cells.
 * @param cell A cell's size in world pixels.
 * @param zoom The camera's zoom.
 * @param look How the bitmap is drawn.
 * @returns How many rectangles were drawn.
 */
function drawArea(pen: HighlightPen, area: PresenceArea, window: CellWindow, cell: number, zoom: number, look: AreaLook): number {
  const { colour } = look;
  const stripes = look.stripe > 0 && cell * zoom >= HIGHLIGHT_STRIPE_MIN_CELL;
  let rects = 0;
  if (look.keyline) {
    const keyline = CANVAS_COLOURS.layerHighlightKeyline;
    pen.fillStyle(keyline.rgb, keyline.alpha);
    rects += stripBoundary(pen, area, window, cell, HIGHLIGHT_KEYLINE_PX / zoom, false);
  }
  pen.fillStyle(colour.rgb, colour.alpha * (look.stripe > 0 && !stripes ? look.flat : look.fill));
  rects += forEachRun(area, window, (x, y, length) => pen.fillRect(x * cell, y * cell, length * cell, cell));
  if (stripes) {
    pen.fillStyle(colour.rgb, colour.alpha * look.stripe);
    rects +=
      2 *
      forEachRun(area, window, (x, y, length) => {
        pen.fillRect(x * cell, (y + 0.125) * cell, length * cell, cell / 4);
        pen.fillRect(x * cell, (y + 0.625) * cell, length * cell, cell / 4);
      });
  }
  pen.fillStyle(colour.rgb, colour.alpha * look.edge);
  return rects + stripBoundary(pen, area, window, cell, look.edgePx / zoom, true);
}

/**
 * Draws a highlight's cells inside a window of its grid: the quietest first, so the cells that show lie over
 * their neighbours' edges: the cells under a tile or prop, the covered cells, then the cells that show.
 *
 * @param pen The pen, a `Graphics` already cleared.
 * @param highlight The highlight.
 * @param window The window, in cells of the highlight's grid.
 * @param zoom The camera's zoom.
 * @returns How many rectangles were drawn.
 */
export function drawHighlight(pen: HighlightPen, highlight: LayerHighlight, window: CellWindow, zoom: number): number {
  const cell = highlight.cellPixels;
  const under: AreaLook = { colour: CANVAS_COLOURS.layerHighlightUnder, fill: HIGHLIGHT_COVERED_FILL, stripe: 0, flat: HIGHLIGHT_COVERED_FILL, edgePx: HIGHLIGHT_UNDER_EDGE_PX, edge: HIGHLIGHT_QUIET_EDGE, keyline: false };
  const covered: AreaLook = { colour: CANVAS_COLOURS.layerHighlightCovered, fill: HIGHLIGHT_COVERED_FILL, stripe: HIGHLIGHT_STRIPE, flat: HIGHLIGHT_COVERED_FLAT, edgePx: HIGHLIGHT_EDGE_PX, edge: HIGHLIGHT_QUIET_EDGE, keyline: true };
  const showing: AreaLook = { colour: CANVAS_COLOURS.layerHighlight, fill: HIGHLIGHT_FILL, stripe: 0, flat: HIGHLIGHT_FILL, edgePx: zoom < HIGHLIGHT_FAR_ZOOM ? HIGHLIGHT_FAR_EDGE_PX : HIGHLIGHT_EDGE_PX, edge: 1, keyline: true };
  return drawArea(pen, highlight.under, window, cell, zoom, under) + drawArea(pen, highlight.covered, window, cell, zoom, covered) + drawArea(pen, highlight.showing, window, cell, zoom, showing);
}

/**
 * The highlight of one scene.
 */
export class LayerHighlightView {
  private readonly graphics: Phaser.GameObjects.Graphics;
  private highlight: LayerHighlight | null = null;
  private dirty = false;
  private zoom = 0;
  private windowX0 = 0;
  private windowY0 = 0;
  private windowX1 = -1;
  private windowY1 = -1;
  private draws = 0;
  private rects = 0;
  private lastMs = 0;

  /**
   * @param scene Scene to draw into.
   * @param depth Depth over every layer of the map.
   */
  public constructor(scene: Phaser.Scene, depth: number) {
    this.graphics = scene.add.graphics().setDepth(depth);
  }

  /**
   * How many times the highlight has been drawn or cleared.
   *
   * @returns The count.
   */
  public get redraws(): number {
    return this.draws;
  }

  /**
   * How many rectangles the last draw made.
   *
   * @returns The count, 0 with nothing drawn.
   */
  public get rectangles(): number {
    return this.rects;
  }

  /**
   * How long the last draw took.
   *
   * @returns Milliseconds.
   */
  public get lastDrawMs(): number {
    return this.lastMs;
  }

  /**
   * Sets the highlight to draw, or none. The same object as last time changes nothing.
   *
   * @param highlight The highlight, or null to draw nothing.
   */
  public setHighlight(highlight: LayerHighlight | null): void {
    if (highlight !== this.highlight) {
      this.highlight = highlight;
      this.dirty = true;
    }
  }

  /**
   * Draws the highlight for the camera's view when it or the zoom changed or the view left the window last
   * drawn. Takes the view as numbers, so a caller each frame allocates nothing.
   *
   * @param viewLeft The view's left edge in world pixels.
   * @param viewTop The view's top edge in world pixels.
   * @param viewWidth The view's width in world pixels.
   * @param viewHeight The view's height in world pixels.
   * @param zoom The camera's zoom.
   */
  public sync(viewLeft: number, viewTop: number, viewWidth: number, viewHeight: number, zoom: number): void {
    const highlight = this.highlight;
    if (highlight === null) {
      if (this.dirty) {
        this.dirty = false;
        this.graphics.clear();
        this.draws += 1;
        this.rects = 0;
      }
      return;
    }
    const cell = highlight.cellPixels;
    const left = Math.floor(viewLeft / cell);
    const top = Math.floor(viewTop / cell);
    const right = Math.floor((viewLeft + viewWidth) / cell);
    const bottom = Math.floor((viewTop + viewHeight) / cell);
    if (!this.dirty && zoom === this.zoom && left >= this.windowX0 && top >= this.windowY0 && right <= this.windowX1 && bottom <= this.windowY1) {
      return;
    }
    const spanX = right - left + 1;
    const spanY = bottom - top + 1;
    this.windowX0 = left - (spanX >> 1);
    this.windowY0 = top - (spanY >> 1);
    this.windowX1 = right + (spanX >> 1);
    this.windowY1 = bottom + (spanY >> 1);
    this.zoom = zoom;
    this.dirty = false;
    const started = performance.now();
    this.graphics.clear();
    this.rects = drawHighlight(this.graphics, highlight, { x0: this.windowX0, y0: this.windowY0, x1: this.windowX1, y1: this.windowY1 }, zoom);
    this.lastMs = performance.now() - started;
    this.draws += 1;
  }

  /**
   * Removes the drawing from the scene.
   */
  public destroy(): void {
    this.graphics.destroy();
  }
}
